import type { AppDispatch, RootState } from "@Reducers/index";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import axiosWrapper, { type ApiPostRequest } from "../services/axiosWrapper";
import { CONSTANTS } from "@CubeComponents/constants/globalVariable";
import { formCategories } from "@Helpers";
import {
  getPortalUserListAction,
  getPortalUserCountAction,
  setPortalDataAction,
  mergePortalDataAction,
  setPortalEntityListAction,
  getCostDatabaseDataAction,
} from "../reducers/portalSetting";
import type { Exact } from "@Helpers/types/exact";
import { resolvePortalURLFrontend } from "@Helpers/localDevelopment";

const getPortalUserList = getPortalUserListAction;
const getPortalUserCount = getPortalUserCountAction;
const setPortalData = setPortalDataAction;
const mergePortalData = mergePortalDataAction;
const setPortalEntityList = setPortalEntityListAction;
const getCostDatabaseData = getCostDatabaseDataAction;

/** The portal's billing, which `/getLayoutData` sends to a signed-in member and `/getPortalData` to nobody. */
async function getPortalBilling() {
  const response = await axiosWrapper.api.get("/portal/getLayoutData");
  return response.data.status ? response.data.portalBilling : undefined;
}

export function getPortalData() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const portalFrontendUrl = resolvePortalURLFrontend(window.location.host);
    const requestData: ApiPostRequest<"/portal/getPortalData"> = {
      value: {
        where: [
          {
            isComplex: false,
            field: "frontendUrl",
            operator: "equal",
            value: portalFrontendUrl,
          },
        ],
      },
    };
    const response = await axiosWrapper.api.post("/portal/getPortalData", requestData);
    // `result` rides the success arm only; the failure arm is the central error handler's body.
    const portalRow = response.data.status ? response.data.result[0] : undefined;
    // `portalDataRow` already types the row (`_id` included); the spread is still taken at the open
    // record type the portal slice is merged as, so only the id read is tightened here.
    const portalData: Record<string, unknown> | undefined = portalRow;
    // Asked once the public row is in, so a sign-in that completed meanwhile is seen: the slice is replaced
    // below, and replacing it without the billing would drop what a member's screens read.
    const { userDetails } = getState();
    const portalBilling = userDetails.id && userDetails.portal?.id ? await getPortalBilling() : undefined;
    let data = {
      ...portalData,
      ...portalBilling,
      logoUrl: `${getUrlForImage({ id: portalRow?._id }, CONSTANTS.PORTAL)}?v=${Date.now()}`,
      signUpLogoUrl: `${getUrlForImage({ id: portalRow?._id }, CONSTANTS.SIGNUP)}?v=${Date.now()}`,
    };
    // `electronAPI` is injected by the desktop shell's preload script and is absent in the browser,
    // hence the optional calls. Named at the read rather than declared globally.
    (
      window as Window & { electronAPI?: { setPortalDataInDesktop?: (d: unknown) => void } }
    ).electronAPI?.setPortalDataInDesktop?.(data);
    dispatch(setPortalData(data));
  };
}

// KNOWN BUG, server side: `/portal/getPortalUsedSeats` answers a single `countDocuments` number —
// every account in the portal — not the per-role `{ roleName, count }` rows the pro/lite split needs
// (portal.mongo.service.ts `getPortalUsedSeats`, and the NOTE on `getPortalUsedSeatsResponse`).
export function getPortalUsedSeats<T extends Exact<ApiPostRequest<"/portal/getPortalUsedSeats">, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.api.post("/portal/getPortalUsedSeats", data);

    const mergedData = {
      usedProSeats: 0,
      usedLiteSeats: 0,
    };

    // The old read asserted that number into an array and threw "resultArray.forEach is not a
    // function", so neither counter ever reached the store. With no role on the count it lands in the
    // same bucket the per-role loop used for every non-`PORTAL_GUEST` role; the lite/guest split
    // stays 0 until the endpoint returns the breakdown.
    if (response.data.status) {
      mergedData.usedProSeats = response.data.result;
    }

    dispatch(mergePortalData(mergedData));
  };
}

export async function changePortalRolePermission<
  T extends Exact<ApiPostRequest<"/portal/changePortalRolePermission">, T>,
>(data: T) {
  const response = await axiosWrapper.api.post("/portal/changePortalRolePermission", data);
  // Returns the BODY, like every sibling here. Handing back the whole AxiosResponse made
  // `res.status` the HTTP status — always 200, so a `{ status: false }` refusal read as success.
  return response.data;
}

export function getPortalUserDetails<T extends Exact<ApiPostRequest<"/portal/getPortalUsers">, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: the thunk is void-returning and every call site reads the list off redux, so
    // returning the promise here would only turn each `dispatch(...)` into a new floating one.
    void axiosWrapper.api.post("/portal/getPortalUsers", data).then((response) => {
      if (response.data.status) {
        // Both members are `.optional()` on the success arm — an empty page answers with neither —
        // and both reducers accept `undefined`, so they are dispatched as they arrive.
        dispatch(getPortalUserList(response.data.result));
        dispatch(getPortalUserCount(response.data.count));
      }
    });
  };
}

export async function changePortalRole<T extends Exact<ApiPostRequest<"/portal/changeUserPortalProfile">, T>>(data: T) {
  // This route uniquely wraps its body — the handler is `return { result: await …Inner(params) }`.
  // That wrap is now DESCRIBED BY the response schema itself (`changeUserPortalProfileResponse` =
  // `{ result: portalNestedWriteResultSchema }`), so re-wrapping it here would count it twice and
  // callers' `response.result.status` would read the outer envelope instead of the nested bag.
  const response = await axiosWrapper.api.post("/portal/changeUserPortalProfile", data);
  return response.data;
}

export function getAllPortalEntity() {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: void-returning thunk, consumers read the entity list off redux.
    void axiosWrapper.api.get("/portal/getPortalEntityList").then((response) => {
      if (response.data.status) {
        dispatch(setPortalEntityList(response.data.result));
      }
    });
  };
}

export function fetchCostDatabaseData<T extends Exact<ApiPostRequest<"/costDatabase/getCostDatabaseData">, T>>(
  data: T,
) {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: neither call site awaits the dispatch; the cost-database list is read off redux.
    void axiosWrapper.api.post("/costDatabase/getCostDatabaseData", data).then((res) => {
      if (res.data.status) {
        dispatch(getCostDatabaseData(res.data.result));
      }
    });
  };
}

export const createCostDatabase = async <T extends Exact<ApiPostRequest<"/costDatabase/createCostDatabase">, T>>(
  data: T,
) => {
  const response = await axiosWrapper.api.post("/costDatabase/createCostDatabase", data);
  return response;
};

export const updateCostDatabase = async <T extends Exact<ApiPostRequest<"/costDatabase/updateCostDatabase">, T>>(
  data: T,
) => {
  const response = await axiosWrapper.api.post("/costDatabase/updateCostDatabase", data);
  return response;
};

// `ids` is the route's own `key`: an ARRAY of `objectIdString` on `deleteCostDatabaseDataRequest`, always an
// array, even for one id.
export const deleteCostDb = (ids: ApiPostRequest<"/costDatabase/deleteCostDatabaseData">["key"]) =>
  axiosWrapper.api.post("/costDatabase/deleteCostDatabaseData", { key: ids });

export const getProjectFormTemplate = async () => {
  const data = {
    resourceType: formCategories.projects,
  } satisfies ApiPostRequest<"/portal/getFormTemplate">;
  const response = await axiosWrapper.api.post("/portal/getFormTemplate", data);
  return response;
};
